// <s-hero-slider>: homepage hero strip, one banner at a time. Loops forward forever (1 → 2 → 1 → 2 …):
// a copy of the first banner sits after the last one, the slider glides onto that copy, then silently jumps
// back to the real first banner, so it never rewinds backwards. Marks the visible banner [data-active] so its
// text and photo animate in, builds the dots (the active dot fills over the autoplay delay), and auto-advances
// (paused only while a finger is on it; resumes on release).
class SHeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.dotsWrap = this.querySelector('[data-hero-dots]');
    this.prev = this.querySelector('[data-hero-prev]');
    this.next = this.querySelector('[data-hero-next]');
    if (!this.track) return;
    this.delay = Number(this.getAttribute('autoplay') || 0);
    this.paused = false;
    this.current = -1;
    this.count = this.track.children.length;

    // Loop copy of banner 1 (hidden from assistive tech and keyboard).
    if (this.count > 1 && !this.track.querySelector('[data-hero-clone]')) {
      const clone = this.track.children[0].cloneNode(true);
      clone.setAttribute('data-hero-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.querySelectorAll('img').forEach((img) => { img.removeAttribute('fetchpriority'); img.loading = 'lazy'; });
      this.track.appendChild(clone);
    }

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.sync()), { passive: true });
    this.track.addEventListener('scrollend', () => this.settle());
    // Only re-align when the width really changes (phones fire resize while the address bar shows/hides on scroll,
    // which kept resetting the autoplay clock so the banner never moved).
    this.lastWidth = this.track.clientWidth;
    window.addEventListener('resize', () => {
      if (this.track.clientWidth === this.lastWidth) return;
      this.lastWidth = this.track.clientWidth;
      this.jump(this.real(this.page()));
      this.current = -1;
      this.sync();
    });

    // Pause only while a finger is on the banner; resume when it lifts or the touch is cancelled.
    // No pause on mouse hover or focus, so a resting cursor or a tapped arrow/dot can't stop autoplay for good.
    this.addEventListener('touchstart', () => (this.paused = true), { passive: true });
    ['touchend', 'touchcancel'].forEach((e) => this.addEventListener(e, () => { this.paused = false; this.lastMove = Date.now(); }, { passive: true }));

    this.buildDots();
    this.sync();
    requestAnimationFrame(() => this.setAttribute('data-ready', ''));

    // Autoplay runs for everyone (phones in battery-saver / "reduce motion" mode used to get a frozen banner);
    // the zoom and text animations stay off for them via CSS.
    if (this.delay > 0 && this.count > 1) {
      this.lastMove = Date.now();
      this.timer = setInterval(() => {
        if (this.paused || document.hidden) { this.lastMove = Date.now(); return; }
        if (Date.now() - this.lastMove < this.delay) return;
        this.step(1);
      }, 250);
    }
  }
  disconnectedCallback() { clearInterval(this.timer); clearTimeout(this.settleTimer); }
  width() { return Math.max(1, this.track.clientWidth); }
  page() { return Math.round(this.track.scrollLeft / this.width()); }
  real(p) { return this.count ? ((p % this.count) + this.count) % this.count : 0; }
  // Instantly move to a banner with no glide and no text re-animation.
  jump(i) {
    this.setAttribute('data-jump', '');
    this.track.scrollTo({ left: i * this.width(), behavior: 'instant' });
    this.current = -1;
    this.sync();
    requestAnimationFrame(() => requestAnimationFrame(() => this.removeAttribute('data-jump')));
  }
  glide(i) {
    this.lastMove = Date.now();
    this.track.scrollTo({ left: i * this.width(), behavior: 'smooth' });
    // Fallback for browsers without scrollend.
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.settle(), 900);
  }
  step(dir) {
    if (this.count < 2) return;
    let p = this.page();
    if (p >= this.count) { this.jump(0); p = 0; }
    if (dir > 0) {
      this.glide(p + 1); // from the last banner this glides onto the copy of banner 1
    } else if (p <= 0) {
      this.jump(this.count); // sit on the copy of banner 1, then glide back to the last banner
      this.glide(this.count - 1);
    } else {
      this.glide(p - 1);
    }
  }
  go(i) { this.glide(this.real(i)); }
  // Once the glide lands on the copy of banner 1, swap to the real banner 1 invisibly.
  settle() {
    if (this.count > 1 && this.page() >= this.count) this.jump(0);
  }
  buildDots() {
    if (!this.dotsWrap) return;
    const label = this.dotsWrap.dataset.label || 'Slide';
    this.dotsWrap.innerHTML = '';
    this.dotsWrap.hidden = this.count <= 1;
    for (let i = 0; i < this.count; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hero-dot';
      b.setAttribute('aria-label', `${label} ${i + 1} / ${this.count}`);
      b.addEventListener('click', () => this.go(i));
      this.dotsWrap.appendChild(b);
    }
  }
  sync() {
    const p = this.page();
    if (p === this.current) return;
    this.current = p;
    this.lastMove = Date.now();
    const shown = this.real(p);
    [...this.track.children].forEach((item, i) => {
      item.toggleAttribute('data-active', i === p);
      if (!item.hasAttribute('data-hero-clone')) item.querySelector('a')?.setAttribute('tabindex', i === p ? '0' : '-1');
    });
    [...(this.dotsWrap?.children || [])].forEach((d, i) => {
      d.setAttribute('aria-current', i === shown ? 'true' : 'false');
    });
    if (this.prev) this.prev.disabled = this.count < 2;
    if (this.next) this.next.disabled = this.count < 2;
  }
}
if (!customElements.get('s-hero-slider')) customElements.define('s-hero-slider', SHeroSlider);
